import { useEffect, useId, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { CaretLeft, Check, Clock, Eye, GearSix, House, Moon, Plugs, Plus, SignOut, Sun, X } from '@phosphor-icons/react'
import { motion, useReducedMotion } from 'motion/react'
import { cn, CountBadge, MenuItem, Popover } from './ui'
import type { PopoverSide } from './ui'
import { updatedLabel } from '../lib/time'
import SunnySymbol from './SunnySymbol'
import { initiatorLabel, taskPreview } from '../lib/workPresentation'
import { spaceLabel } from '../lib/productNavigation'
import type { SidebarSpace, TaskJson, UserJson } from '../types/models'

function initials(name: string | null | undefined) {
  const words = (name || 'Sunny').trim().split(/\s+/).filter(Boolean)
  return words.slice(0, 2).map((word) => word[0]).join('').toUpperCase()
}

// Identity marks use circular geometry. Size by `size` (px) or by a
// `size-N` class in `className`.
export type SpaceMarkSpace = {
  name?: string | null
  icon?: string | null
  avatar?: string | null
}

export type SpaceMarkProps = {
  space?: SpaceMarkSpace | null
  size?: number
  className?: string
}

export function SpaceMark({ space, size, className = '' }: SpaceMarkProps) {
  const mark = space?.icon?.trim() || space?.avatar?.trim() || initials(space?.name)
  const style = size ? { width: size, height: size, fontSize: Math.round(size * 0.33) } : undefined
  return (
    <div
      className={cn('grid shrink-0 place-items-center overflow-hidden rounded-full bg-fg font-display font-medium text-primary-fg', !size && 'size-9 text-xs', className)}
      style={style}
      aria-hidden="true"
    >{mark}</div>
  )
}

export type PersonMarkProps = {
  name?: string | null
  size?: number
  className?: string
}

export function PersonMark({ name, size = 28, className = '' }: PersonMarkProps) {
  return (
    <div
      className={cn('grid shrink-0 place-items-center overflow-hidden rounded-full bg-person font-display font-medium text-person-fg', className)}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
      aria-hidden="true"
    >{initials(name)}</div>
  )
}

// AI identity is the same sun as the app mark, static at avatar sizes.
export type AiMarkProps = {
  size?: number
  className?: string
}

export function AiMark({ size = 28, className = '' }: AiMarkProps) {
  return (
    <span className={cn('grid shrink-0 place-items-center', className)} style={{ width: size, height: size }} aria-hidden="true">
      <SunnySymbol size={Math.round(size * 0.85)} />
    </span>
  )
}

export type SunnyMarkProps = {
  wordmark?: boolean
  size?: number
  animated?: boolean
  className?: string
}

export function SunnyMark({ wordmark = false, size = 28, animated = false, className = '' }: SunnyMarkProps) {
  return (
    <Link href="/" aria-label="Sunny" className={cn('flex items-center gap-2', className)}>
      <SunnySymbol size={size} animated={animated} />
      {wordmark && <span className="font-display text-base font-semibold tracking-[-0.018em]">sunny</span>}
    </Link>
  )
}

// A task stays in the Done section after any terminal run, but its outcome should still be
// visible. Otherwise failed and cancelled work looks indistinguishable from success.
export type TerminalOutcome = 'failed' | 'cancelled' | 'lost'

export function taskDisplayStatus<S extends string>(task: { status: S; outcome?: string | null }): S | TerminalOutcome {
  return task.status === 'done' && isTerminalOutcome(task.outcome) ? task.outcome : task.status
}

function isTerminalOutcome(outcome: string | null | undefined): outcome is TerminalOutcome {
  return outcome === 'failed' || outcome === 'cancelled' || outcome === 'lost'
}

// One task's state as a circle. Only `needs_you` is orange.
function StatusCircle({ status, size = 40, className = '' }: { status: string; size?: number; className?: string }) {
  const base = 'grid shrink-0 place-items-center rounded-full'
  const icon = Math.round(size * 0.45)
  if (status === 'needs_you') {
    return <div className={cn(base, 'bg-warning-soft font-display font-semibold text-warning', className)} style={{ width: size, height: size, fontSize: icon }} aria-hidden="true">!</div>
  }
  if (status === 'working') {
    return <div className={cn(base, 'bg-success-soft', className)} style={{ width: size, height: size }} aria-hidden="true"><span className="pulse-dot rounded-full bg-success" style={{ width: size * 0.22, height: size * 0.22 }} /></div>
  }
  if (status === 'done') {
    return <div className={cn(base, 'bg-success-soft text-success', className)} style={{ width: size, height: size }} aria-hidden="true"><Check weight="bold" style={{ width: icon, height: icon }} /></div>
  }
  if (['failed', 'cancelled', 'lost'].includes(status)) {
    return <div className={cn(base, 'bg-danger-soft text-danger', className)} style={{ width: size, height: size }} aria-hidden="true"><X weight="bold" style={{ width: icon, height: icon }} /></div>
  }
  const Icon = status === 'watching' ? Eye : Clock
  return <div className={cn(base, 'bg-muted-300 text-muted-800', className)} style={{ width: size, height: size }} aria-hidden="true"><Icon style={{ width: icon, height: icon }} /></div>
}

// Tasks use the existing status glyph and never load task artwork.
export type TaskMarkTask = {
  status: string
  outcome?: string | null
  title?: string
}

export type TaskMarkProps = {
  task: TaskMarkTask
  size?: number
  className?: string
}

export function TaskMark({ task, size = 40, className = '' }: TaskMarkProps) {
  return <StatusCircle status={taskDisplayStatus(task)} size={size} className={className} />
}

// Sidebar row: avatar · name · one-line status. Compact selection refracts the sidebar mesh.
export type SpaceRowSpace = SpaceMarkSpace & Pick<SidebarSpace, 'path' | 'status_line'> & { personal?: boolean }

export type SpaceRowProps = {
  space: SpaceRowSpace
  active?: boolean
  unread?: number
  badge?: number
  size?: number
  compact?: boolean
}

export function SpaceRow({ space, active, unread = 0, badge = 0, size = 36, compact = false }: SpaceRowProps) {
  return (
    <Link
      href={space.path}
      aria-current={active ? 'page' : undefined}
      className={cn('flex min-w-0 items-center gap-2.5 rounded-row text-left hover:bg-hover', compact ? 'h-10 shrink-0 border border-transparent px-2' : 'min-h-16 p-2.5', active && (compact ? 'sunny-sidebar-selection' : 'bg-select hover:bg-select'))}
    >
      <SpaceMark space={space} size={size} className={cn(compact && !active && 'bg-chip text-muted-700', compact && active && 'bg-accent-100 text-accent')} />
      <div className="min-w-0 flex-1">
        <p className={cn('truncate font-medium', compact ? 'text-sm/5' : 'text-base/6 sm:text-sm/5')}>{spaceLabel(space)}</p>
        {!compact && <p className="truncate pt-0.5 text-xs/5 text-muted-600">{space.status_line}</p>}
      </div>
      {badge > 0 && <CountBadge>{badge}</CountBadge>}
      {!badge && unread > 0 && !active && <span className="size-[7px] shrink-0 rounded-full bg-accent" aria-label={`${unread} unread`} />}
    </Link>
  )
}

function readTheme(): 'light' | 'dark' {
  if (typeof document === 'undefined') return 'light'
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}

// The signed-in user row: opens everything that is not a Space.
export type UserMenuProps = {
  user: UserJson
  unread?: number
  side?: PopoverSide
}

export function UserMenu({ user, unread = 0, side = 'top' }: UserMenuProps) {
  const { system_admin, switchable_accounts = [] } = usePage().props
  const [theme, setTheme] = useState<'light' | 'dark'>(readTheme)
  useEffect(() => setTheme(readTheme()), [])
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark'
    document.documentElement.classList.toggle('dark', next === 'dark')
    try { localStorage.setItem('grid-theme', next) } catch { /* private mode */ }
    setTheme(next)
  }
  const name = user.display
  const sub = user.bot_account ? `Bot account · @${user.bot_handle}` : user.email_address || user.phone_number || ''
  return (
    <Popover
      side={side}
      wrapperClassName="w-full"
      trigger={(open, toggle) => (
        <button type="button" onClick={toggle} aria-expanded={open} aria-haspopup="menu" className={cn('flex h-11 w-full items-center gap-2.5 rounded-row px-2 text-left hover:bg-hover', open && 'bg-select')}>
          <PersonMark name={name} size={28} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm/5 font-medium">{name}</p>
            {sub && sub !== name && <p className="truncate text-xs/4 text-muted-600">{sub}</p>}
          </div>
          <span className="relative shrink-0 text-muted-600"><GearSix className="size-[15px]" aria-hidden="true" />{unread > 0 && <span className="absolute -top-1 -right-1 size-[7px] rounded-full bg-accent" aria-hidden="true" />}</span>
        </button>
      )}
    >
      <div role="menu" className="flex flex-col">
        {system_admin && <>
          <p className="px-2.5 py-1.5 text-sm font-medium text-muted-600">Switch account</p>
          <div className="max-h-60 overflow-y-auto" role="group" aria-label="Switch account">
            {switchable_accounts.map((account) => <MenuItem key={account.id} icon={account.current ? Check : undefined} aria-current={account.current ? 'true' : undefined} onClick={() => {
              if (account.current) return
              account.personal ? router.delete('/admin/account_switch') : router.post(`/admin/bot_accounts/${account.id}/switch`)
            }}>{account.name}{account.personal ? ' (you)' : ` · @${account.handle}`}</MenuItem>)}
          </div>
          <div className="my-1 hairline-b" aria-hidden="true" />
        </>}
        <MenuItem as="a" href="/home" icon={House} badge={unread}>Home</MenuItem>
        <MenuItem as="a" href="/account/profile" icon={GearSix}>{user.profile_complete ? 'Your profile' : 'Add your name'}</MenuItem>
        <MenuItem as="a" href="/integrations" icon={Plugs}>Apps &amp; accounts</MenuItem>
        {(system_admin || user.admin) && <MenuItem as="a" href="/admin" icon={GearSix}>System admin</MenuItem>}
        <div className="my-1 hairline-b" aria-hidden="true" />
        <MenuItem onClick={toggleTheme} icon={theme === 'dark' ? Sun : Moon}>{theme === 'dark' ? 'Light appearance' : 'Dark appearance'}</MenuItem>
        <MenuItem onClick={() => router.delete('/session')} icon={SignOut}>Sign out</MenuItem>
      </div>
    </Popover>
  )
}

export type SpacesSidebarProps = {
  spaces?: SidebarSpace[]
  user?: UserJson | null
  path: string
  unread?: number
  className?: string
}

export function SpacesSidebar({ spaces = [], user, path, unread = 0, className = '' }: SpacesSidebarProps) {
  const needsYou = spaces.reduce((total, space) => total + (space.needs_you || 0), 0)
  return (
    <aside className={cn('min-h-0 overflow-hidden flex-col gap-4 px-[14px] pt-[18px] pb-[14px]', className)} aria-label="Spaces">
      <div className="flex h-24 shrink-0 items-center justify-center"><SunnyMark size={88} animated /></div>
      <div className="flex min-h-0 flex-1 flex-col gap-1">
        <Link href="/home" aria-current={path === '/home' || path === '/' ? 'page' : undefined} className={cn('mb-1 flex h-10 shrink-0 items-center gap-2.5 rounded-row border border-transparent px-2 text-sm/5 font-medium hover:bg-hover', (path === '/home' || path === '/') && 'sunny-sidebar-selection')}>
          <span className="grid size-[22px] place-items-center rounded-full bg-chip text-muted-700"><House className="size-3.5" weight="fill" aria-hidden="true" /></span>
          <span className="min-w-0 flex-1 truncate">Home</span>
          {needsYou > 0 && <CountBadge>{needsYou}</CountBadge>}
        </Link>
        <div className="flex h-6 shrink-0 items-center justify-between px-1.5">
          <span className="text-xs font-medium text-muted-600">Spaces</span>
          <Link href="/spaces/new" aria-label="New Space" className="grid size-6 place-items-center rounded-full text-muted-600 hover:bg-hover hover:text-fg"><Plus className="size-4" weight="bold" /></Link>
        </div>
        <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto" aria-label="Your Spaces">
          {[...spaces].sort((a, b) => Number(b.personal) - Number(a.personal)).map((space) => <SpaceRow key={space.id} space={space} active={path === space.path || path.startsWith(`${space.path}/`)} unread={space.unread} badge={space.needs_you} size={22} compact />)}
          {spaces.length === 0 && <p className="px-2 py-2 text-xs text-muted-600">No spaces yet.</p>}
        </nav>
      </div>
      {user && <div className="shrink-0 border-t border-line pt-2"><UserMenu user={user} unread={unread} /></div>}
    </aside>
  )
}

// Messages-style task row for the Tasks column: status circle · title + time · last line.
export type TaskRowProps = {
  task: TaskJson
  active?: boolean
  context?: string | null
  className?: string
}

export function TaskRow({ task, active, context, className = '' }: TaskRowProps) {
  const preview = taskPreview(task)
  const statusLabel = { working: 'Working', waiting: 'Waiting', watching: 'Watching', todo: 'To do', needs_you: 'Needs you', done: 'Completed', failed: 'Could not finish', lost: 'Could not finish', cancelled: 'Cancelled' }[taskDisplayStatus(task)]
  return (
    <Link
      href={task.path}
      aria-current={active ? 'page' : undefined}
      className={cn('flex items-center gap-2.5 rounded-pane p-2 hover:bg-hover', active && 'bg-select hover:bg-select', task.status === 'done' && 'text-muted-700', className)}
    >
      <TaskMark task={task} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5 text-sm/5 font-medium">
            {(task.chat_unread ?? 0) > 0 && !active && <span className="size-[7px] shrink-0 rounded-full bg-accent" aria-label="unread" />}
            <span className="truncate">{task.title}</span>
          </span>
          <span className="shrink-0 whitespace-nowrap text-[11px] text-muted-600">{task.due_at && task.status !== 'done' ? `due ${updatedLabel(task.due_at)}` : updatedLabel(task.updated_at)}</span>
        </div>
        <p className="mt-0.5 line-clamp-2 text-xs/5 text-muted-600">{[statusLabel, initiatorLabel(task.initiator), context, preview].filter(Boolean).join(' · ')}</p>
      </div>
      {task.assignee?.kind === 'user' && <PersonMark name={task.assignee.name} size={22} className="shrink-0" />}
    </Link>
  )
}

// Toolbar segmented control (26px, 7px radius). `tabs` = [{ key, label, badge }].
export type SegmentedTab<K extends string = string> = {
  key: K
  label: ReactNode
  badge?: number
  disabled?: boolean
}

export type SegmentedProps<K extends string = string> = {
  tabs: SegmentedTab<K>[]
  value: K
  onChange: (key: K) => void
  className?: string
}

export function Segmented<K extends string = string>({ tabs, value, onChange, className = '' }: SegmentedProps<K>) {
  const id = useId()
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const reduced = useReducedMotion()
  const seek = (from: number, direction: number) => {
    let next = from
    for (let step = 0; step < tabs.length; step += 1) {
      next = (next + direction + tabs.length) % tabs.length
      if (!tabs[next].disabled) return next
    }
    return from
  }
  const select = (next: number) => {
    if (!tabs[next] || tabs[next].disabled) return
    refs.current[next]?.focus()
    onChange(tabs[next].key)
  }
  return (
    <div className={cn('chrome-control flex h-8 max-w-full gap-0.5 overflow-x-auto rounded-pill p-0.5 text-xs font-medium [--selection-surface:var(--surface)]', className)} role="tablist" aria-label="View">
      {tabs.map((tab, tabIndex) => (
        <button
          key={tab.key}
          ref={(node) => { refs.current[tabIndex] = node }}
          type="button"
          role="tab"
          disabled={tab.disabled}
          aria-selected={value === tab.key}
          aria-disabled={tab.disabled || undefined}
          tabIndex={value === tab.key ? 0 : -1}
          onClick={() => !tab.disabled && onChange(tab.key)}
          onKeyDown={(event) => {
            if (['ArrowRight', 'ArrowDown'].includes(event.key)) { event.preventDefault(); select(seek(tabIndex, 1)) }
            else if (['ArrowLeft', 'ArrowUp'].includes(event.key)) { event.preventDefault(); select(seek(tabIndex, -1)) }
            else if (event.key === 'Home') { event.preventDefault(); select(seek(tabs.length - 1, 1)) }
            else if (event.key === 'End') { event.preventDefault(); select(seek(0, -1)) }
          }}
          className={cn('relative flex shrink-0 items-center gap-1.5 rounded-pill px-2.5 outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring', value === tab.key ? 'text-fg' : 'text-muted-600 hover:text-fg')}
        >
          {value === tab.key && <motion.span layoutId={`segmented-${id}`} aria-hidden="true" className="chrome-selection absolute inset-0 rounded-pill" transition={reduced ? { duration: 0 } : { duration: 0.18, ease: [0.22, 1, 0.36, 1] }} />}
          <span className="relative">{tab.label}</span>
          {(tab.badge ?? 0) > 0 && <CountBadge className="relative h-[15px] min-w-[15px] px-[5px]">{tab.badge}</CountBadge>}
        </button>
      ))}
    </div>
  )
}

// Shared page gutters and a quiet dividing line anchor each workspace header.
export type ToolbarBack = { href: string; label: string }

export type ToolbarProps = {
  title?: ReactNode
  subtitle?: ReactNode
  tag?: ReactNode
  back?: ToolbarBack | null
  leading?: ReactNode
  actions?: ReactNode
  stacked?: boolean
  divider?: boolean
  className?: string
}

export function Toolbar({ title, subtitle, tag, back, leading, actions, stacked = false, divider = true, className = '' }: ToolbarProps) {
  return (
    <div className={cn('chrome-toolbar flex shrink-0 items-center justify-between gap-3 px-4 sm:px-6', divider && 'border-b border-line', stacked ? 'h-[72px]' : 'h-14', className)}>
      <div className="flex min-w-0 items-center gap-2.5">
        {back && (
          <Link href={back.href} className="-ml-2 flex h-7 items-center gap-0.5 rounded-row pr-2 pl-1 text-xs text-muted-700 hover:bg-hover hover:text-fg lg:hidden" aria-label={`Back to ${back.label}`}>
            <CaretLeft className="size-3.5" weight="bold" aria-hidden="true" />{back.label}
          </Link>
        )}
        {leading}
        <div className={cn('flex min-w-0', stacked ? 'flex-col gap-px' : 'items-center gap-2.5')}>
          <div className="flex min-w-0 items-center gap-2.5">
            {title && <h1 className={cn('min-w-0 truncate font-display font-semibold tracking-[-0.018em]', stacked ? 'text-xl' : 'text-base')}>{title}</h1>}
            {tag}
          </div>
          {subtitle && <p className={cn('min-w-0 truncate text-xs/5 text-muted-600', !stacked && 'hidden sm:block')}>{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
    </div>
  )
}

export type SectionHeaderProps = {
  id?: string
  title: ReactNode
  meta?: ReactNode
  className?: string
}

export function SectionHeader({ id, title, meta, className = '' }: SectionHeaderProps) {
  return (
    <div className={cn('flex items-center justify-between gap-4 px-2 pt-2 pb-3', className)}>
      <h2 id={id} className="text-sm font-medium text-fg">{title}</h2>
      {meta && <p className="text-xs text-muted-600 tabular-nums">{meta}</p>}
    </div>
  )
}


// Where the page sits, for `aria-current` in the sidebar.
export function useCurrentPath(): string {
  const { url } = usePage()
  return typeof url === 'string' ? url.split('?')[0] : (typeof window !== 'undefined' ? window.location.pathname : '/')
}
